import { useState, useMemo, useCallback, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { LayoutGrid, Settings2, TrendingUp, Users, Star, MapPin, Zap, BarChart3, ChevronRight, Heart, Tag } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { generateDisplayName } from '@shared/utils/displayName';

import UsualsWidget from '@/components/widgets/UsualsWidget';
import KudozWidget from '@/components/widgets/KudozWidget';
import FriendsWidget from '@/components/widgets/FriendsWidget';
import NearbyWidget from '@/components/widgets/NearbyWidget';
import DealsWidget from '@/components/widgets/DealsWidget';
import StatsWidget from '@/components/widgets/StatsWidget';
import type { MyEatzyWidgetConfig } from '@/components/widgets/types';
import { DEFAULT_WIDGET_CONFIG } from '@/components/widgets/types';

// ─── Constants ─────────────────────────────────────────────────────────────────

const HOME_TAB_OPTIONS = [
  { value: 'feed', label: 'Feed' },
  { value: 'menus', label: 'Restaurants' },
  { value: 'friends', label: 'Friends' },
  { value: 'myeatzy', label: 'My Eatzy' },
] as const;

const WIDGET_META: Record<keyof MyEatzyWidgetConfig['widgets'], { label: string; description: string; icon: React.ComponentType<{ className?: string }> }> = {
  usuals: { label: 'Your Usuals', description: 'Quick-reorder your favourite spots', icon: TrendingUp },
  kudoz: { label: 'Kudoz & Rewards', description: 'Pending rewards and wallet balance', icon: Zap },
  friends: { label: 'Friends Activity', description: 'Latest reviews from people you follow', icon: Users },
  nearby: { label: 'Nearby', description: 'Restaurants close to you right now', icon: MapPin },
  deals: { label: 'Deals & Offers', description: 'Active promotions and Kudoz offers', icon: Star },
  stats: { label: 'My Stats', description: 'Orders, reviews, likes and lifetime spend', icon: BarChart3 },
};

// ─── Preferences API shape ─────────────────────────────────────────────────────

interface UserPreferencesResponse {
  myEatzyConfig: MyEatzyWidgetConfig | null;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function mergeConfig(raw: unknown): MyEatzyWidgetConfig {
  if (!raw || typeof raw !== 'object') return DEFAULT_WIDGET_CONFIG;
  const r = raw as Partial<MyEatzyWidgetConfig>;
  const w = (r.widgets && typeof r.widgets === 'object') ? r.widgets : {};
  return {
    widgets: {
      usuals: (w as Partial<MyEatzyWidgetConfig['widgets']>).usuals ?? DEFAULT_WIDGET_CONFIG.widgets.usuals,
      kudoz: (w as Partial<MyEatzyWidgetConfig['widgets']>).kudoz ?? DEFAULT_WIDGET_CONFIG.widgets.kudoz,
      friends: (w as Partial<MyEatzyWidgetConfig['widgets']>).friends ?? DEFAULT_WIDGET_CONFIG.widgets.friends,
      nearby: (w as Partial<MyEatzyWidgetConfig['widgets']>).nearby ?? DEFAULT_WIDGET_CONFIG.widgets.nearby,
      deals: (w as Partial<MyEatzyWidgetConfig['widgets']>).deals ?? DEFAULT_WIDGET_CONFIG.widgets.deals,
      stats: (w as Partial<MyEatzyWidgetConfig['widgets']>).stats ?? DEFAULT_WIDGET_CONFIG.widgets.stats,
    },
    homeTab: typeof r.homeTab === 'string' ? r.homeTab : DEFAULT_WIDGET_CONFIG.homeTab,
  };
}

// ─── Widget Section Wrapper ────────────────────────────────────────────────────

function WidgetSection({ title, icon: Icon, children, onNavigate, navLabel }: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  onNavigate?: () => void;
  navLabel?: string;
}) {
  return (
    <section className="space-y-3">
      <div className="px-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-muted-foreground" />
          <h2 className="font-semibold text-sm">{title}</h2>
        </div>
        {onNavigate && navLabel && (
          <Button variant="ghost" size="sm" className="text-xs text-muted-foreground gap-1 -mr-2" onClick={onNavigate}>
            {navLabel}
            <ChevronRight className="w-3 h-3" />
          </Button>
        )}
      </div>
      {children}
    </section>
  );
}

// ─── Customize Sheet ───────────────────────────────────────────────────────────

interface CustomizeSheetProps {
  open: boolean;
  onClose: () => void;
  config: MyEatzyWidgetConfig;
  onSave: (config: MyEatzyWidgetConfig) => void;
  saving: boolean;
}

function CustomizeSheet({ open, onClose, config, onSave, saving }: CustomizeSheetProps) {
  const [local, setLocal] = useState<MyEatzyWidgetConfig>(config);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (open) {
      setLocal(config);
      setDirty(false);
    }
  }, [open, config]);

  const toggleWidget = (key: keyof MyEatzyWidgetConfig['widgets']) => {
    setLocal(prev => ({ ...prev, widgets: { ...prev.widgets, [key]: !prev.widgets[key] } }));
    setDirty(true);
  };

  const setHomeTab = (value: string) => {
    setLocal(prev => ({ ...prev, homeTab: value }));
    setDirty(true);
  };

  const handleSave = () => {
    setDirty(false);
    onSave(local);
    onClose();
  };

  const handleCancel = () => {
    setLocal(config);
    setDirty(false);
    onClose();
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) {
      if (dirty) {
        setDirty(false);
        onSave(local);
      }
      onClose();
    }
  };

  const widgetKeys = Object.keys(WIDGET_META) as Array<keyof MyEatzyWidgetConfig['widgets']>;

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <LayoutGrid className="w-5 h-5 text-primary" />
            Customize My Eatzy
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-5 pb-6">
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Default home tab</h3>
            <RadioGroup value={local.homeTab} onValueChange={setHomeTab} className="grid grid-cols-2 gap-2">
              {HOME_TAB_OPTIONS.map(opt => (
                <div key={opt.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={opt.value} id={`tab-${opt.value}`} />
                  <Label htmlFor={`tab-${opt.value}`} className="text-sm font-normal cursor-pointer">{opt.label}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <Separator />

          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Widgets</h3>
            {widgetKeys.map((key) => {
              const meta = WIDGET_META[key];
              const Icon = meta.icon;
              return (
                <div key={key} className="flex items-center justify-between gap-3 py-1">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-foreground" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-sm">{meta.label}</div>
                      <div className="text-xs text-muted-foreground truncate">{meta.description}</div>
                    </div>
                  </div>
                  <Switch
                    checked={local.widgets[key]}
                    onCheckedChange={() => toggleWidget(key)}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex gap-3 pb-2">
          <Button variant="outline" className="flex-1" onClick={handleCancel}>Cancel</Button>
          <Button className="flex-1" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function MyEatzy() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [sheetOpen, setSheetOpen] = useState(false);

  const { data: prefsData } = useQuery<UserPreferencesResponse>({
    queryKey: ['/api/user/preferences'],
    staleTime: 60_000,
  });

  const config = useMemo((): MyEatzyWidgetConfig => mergeConfig(prefsData?.myEatzyConfig), [prefsData]);

  const mutation = useMutation({
    mutationFn: (newConfig: MyEatzyWidgetConfig) =>
      apiRequest('PUT', '/api/user/preferences', { myEatzyConfig: newConfig }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/preferences'] });
    },
    onError: () => {
      toast({ title: 'Could not save preferences', variant: 'destructive' });
    },
  });

  const handleSave = useCallback((newConfig: MyEatzyWidgetConfig) => {
    mutation.mutate(newConfig);
  }, [mutation]);

  const displayName = user
    ? generateDisplayName(user.firstName, user.lastName, user.displayName ?? user.name)
    : 'there';

  const { widgets } = config;
  const anyWidgetOn = Object.values(widgets).some(Boolean);

  return (
    <div className="flex flex-col min-h-screen pb-20">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LayoutGrid className="w-5 h-5 text-primary" />
          <span className="font-semibold text-base">My Eatzy</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSheetOpen(true)}
          aria-label="Customize"
        >
          <Settings2 className="w-5 h-5" />
        </Button>
      </header>

      <div className="space-y-6 py-4">
        {/* Greeting */}
        <div className="px-4">
          <p className="text-muted-foreground text-sm">
            Hey, <span className="font-semibold text-foreground">{displayName}</span>. Here's your Eatzy at a glance.
          </p>
        </div>

        {/* Quick actions */}
        <div className="px-4">
          <div className="flex gap-2 flex-wrap">
            {([
              { label: 'Feed', onClick: () => navigate('/'), icon: Heart },
              { label: 'Rewards', onClick: () => navigate('/rewards'), icon: Zap },
              { label: 'Profile', onClick: () => navigate('/profile'), icon: Users },
            ] as const).map(({ label, onClick, icon: Icon }) => (
              <Button key={label} variant="outline" size="sm" className="gap-1.5" onClick={onClick}>
                <Icon className="w-3.5 h-3.5" />
                {label}
              </Button>
            ))}
          </div>
        </div>

        {/* Widgets */}
        {!anyWidgetOn ? (
          <div className="px-4 py-10 flex flex-col items-center gap-3 text-center">
            <LayoutGrid className="w-10 h-10 text-muted-foreground/50" />
            <p className="text-muted-foreground text-sm max-w-xs">No widgets are enabled. Tap the customize button to turn some on.</p>
            <Button variant="outline" size="sm" onClick={() => setSheetOpen(true)}>
              <Settings2 className="w-3.5 h-3.5 mr-1" />
              Customize
            </Button>
          </div>
        ) : (
          <>
            {widgets.usuals && (
              <WidgetSection title="Your Usuals" icon={TrendingUp} onNavigate={() => navigate('/restaurants')} navLabel="All restaurants">
                <UsualsWidget />
              </WidgetSection>
            )}
            {widgets.deals && (
              <WidgetSection title="Deals & Offers" icon={Tag} onNavigate={() => navigate('/restaurants')} navLabel="Browse">
                <DealsWidget />
              </WidgetSection>
            )}
            {widgets.friends && (
              <WidgetSection title="Friends Activity" icon={Users} onNavigate={() => navigate('/friends')} navLabel="Friends">
                <FriendsWidget />
              </WidgetSection>
            )}
            {widgets.nearby && (
              <WidgetSection title="New Nearby" icon={MapPin} onNavigate={() => navigate('/restaurants')} navLabel="See all">
                <NearbyWidget />
              </WidgetSection>
            )}
            {widgets.kudoz && (
              <WidgetSection title="Kudoz & Rewards" icon={Zap} onNavigate={() => navigate('/rewards')} navLabel="View all">
                <KudozWidget />
              </WidgetSection>
            )}
            {widgets.stats && (
              <WidgetSection title="My Stats" icon={BarChart3} onNavigate={() => navigate('/profile')} navLabel="Profile">
                <StatsWidget />
              </WidgetSection>
            )}
          </>
        )}
      </div>

      <CustomizeSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        config={config}
        onSave={handleSave}
        saving={mutation.isPending}
      />
    </div>
  );
}
